import { getCookie, deleteCookie } from './cookie';

const UTM_PARAM_PATTERN = /^utm_[a-z0-9_]+$/i;

/**
 * Páginas de criação onde a UTM da URL pode ser capturada (first touch).
 * Inclui legado `/me/events/new/what` e draft-first `/me/events/{id}/what`.
 */
export const isCreateEventWhatPath = (pathname: string): boolean =>
	pathname === '/me/events/new/what' ||
	/^\/me\/events\/[^/]+\/what$/.test(pathname);


const extractValidUtmParams = (value: string): URLSearchParams => {
    const inputParams = new URLSearchParams(value.replace(/^\?/, ''));
    const utmParams = new URLSearchParams();

    inputParams.forEach((paramValue, key) => {
        if (UTM_PARAM_PATTERN.test(key)) {
            utmParams.append(key, paramValue);
        }
    });

    return utmParams;
};

export const normalizeUtmQueryString = (
    value: string | null | undefined
): string | null => {
    if (!value) {
        return null;
    }

    const originalParams = extractValidUtmParams(value);
    if (Array.from(originalParams.keys()).length > 0) {
        return originalParams.toString();
    }

    try {
        const decodedParams = extractValidUtmParams(decodeURIComponent(value));
        return Array.from(decodedParams.keys()).length > 0
            ? decodedParams.toString()
            : null;
    } catch {
        return null;
    }
};

/**
 * 
 * @param url URL interna para adicionar parâmetros UTM
 * @param utmString String de parâmetros UTM (opcional, se não fornecida, tenta obter do cookie)
 * @returns URL com parâmetros UTM adicionados
 */
export const addUtmToInternalLink = (url: string, utmString?: string | null): string => {
    if (url.includes('utm_')) {
        return url;
    }

    const utm = utmString || getCookie('uticketUtm');

    if (!utm) {
        return url;
    }

    try {
        const urlObj = new URL(url);

        const utmParams = new URLSearchParams(utm);

        utmParams.forEach((value, key) => {
            urlObj.searchParams.append(key, value);
        });

        return urlObj.toString();
    } catch (error) {
        const hasQueryParams = url.includes('?');
        return `${url}${hasQueryParams ? '&' : '?'}${utm}`;
    }
};

/**
 * 
 * @param url URL para extrair parâmetros UTM
 * @returns Objeto com parâmetros UTM extraídos
 */
export const extractUtmParams = (url: string) => {
    try {
        const urlObj = new URL(url);
        const params = new URLSearchParams(urlObj.search);

        return {
            source: params.get('utm_source'),
            medium: params.get('utm_medium'),
            campaign: params.get('utm_campaign'),
            term: params.get('utm_term'),
            content: params.get('utm_content'),
        };
    } catch (error) {
        return {
            source: null,
            medium: null,
            campaign: null,
            term: null,
            content: null,
        };
    }
};

/**
 * 
 * @param url URL para verificar
 * @returns true se a URL contém parâmetros UTM, false caso contrário
 */
export const hasUtmParams = (url: string): boolean => {
    try {
        const urlObj = new URL(url);
        const params = new URLSearchParams(urlObj.search);

        return Array.from(params.keys()).some(key => key.startsWith('utm_'));
    } catch (error) {
        return false;
    }
};

/**
 * @param hostname Opcional - hostname a ser verificado
 * @returns true se o ambiente for localhost, PR ou QA
 */
const isAllowedEnvironment = (hostname?: string): boolean => {
    const host = hostname || window.location.hostname;
    const isLocalhost = host === 'localhost' || host === '127.0.0.1';
    const isPrUticketDev = host === 'pr.uticket.dev.br' || host.endsWith('.pr.uticket.dev.br');
    const isQaUticketDev = host === 'uticket.dev.br' || (host.includes('uticket.dev.br') && !isPrUticketDev);
    
    return isLocalhost || isPrUticketDev || isQaUticketDev;
};

/**
 * Limpa todos os dados de UTM armazenados no navegador
 * 
 * Útil para depuração e testes locais.
 * Esta função limpa:
 * 1. O localStorage (Zustand persistido)
 * 2. O cookie de UTM
 * 3. A flag de propagação no sessionStorage
 * 
 * IMPORTANTE: Esta função só funciona em localhost, pr.uticket.dev.br e uticket.dev.br
 * 
 * @param hostname Hostname atual para definir o domínio do cookie corretamente
 * @returns Um objeto indicando o que foi limpo ou null se não estiver em ambiente permitido
 */
export const clearAllUtmData = (hostname?: string): {localStorage: boolean, cookie: boolean, sessionStorage: boolean} | null => {
    if (!isAllowedEnvironment(hostname)) {
        console.warn('Função clearAllUtmData só pode ser executada em localhost, pr.uticket.dev.br ou uticket.dev.br');
        return null;
    }
    
    localStorage.removeItem('uticket-utm-storage');

    const host = hostname || window.location.hostname;
    const isPrEnvironment = host === 'pr.uticket.dev.br' || host.endsWith('.pr.uticket.dev.br');
    const isDevEnvironment = host === 'uticket.dev.br' || (host.includes('uticket.dev.br') && !isPrEnvironment);
    const isProdEnvironment = host.includes('uticket.com.br');

    deleteCookie('uticketUtm');

    if (isPrEnvironment) {
        deleteCookie('uticketUtm', '.pr.uticket.dev.br');
    } else if (isDevEnvironment) {
        deleteCookie('uticketUtm', '.uticket.dev.br');
    } else if (isProdEnvironment) {
        deleteCookie('uticketUtm', '.uticket.com.br');
    }
    
    sessionStorage.removeItem('uticket-utm-propagation-active');
    
    return {
        localStorage: true,
        cookie: true,
        sessionStorage: true
    };
};
